import type * as React from "react";

import { userFacingErrorMessage } from "@/shared/http";

/**
 * The provider's `error` parameter is a fixed enumeration (RFC 6749 §4.1.2.1), so it is
 * mapped rather than echoed. An unrecognized value falls back to the provider's
 * `error_description`, then to {@link GENERIC_PROVIDER_ERROR}.
 */
export const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  access_denied: "The authorization was cancelled.",
  invalid_request: "The authorization request was malformed.",
  invalid_scope: "The authorization asked for permissions that were refused.",
  server_error: "The provider hit an internal error.",
  temporarily_unavailable:
    "The provider is temporarily unavailable. Try again shortly.",
  unauthorized_client: "This Roboto deployment is not an authorized client.",
  unsupported_response_type: "The provider refused this authorization flow.",
};

export const GENERIC_PROVIDER_ERROR = "The provider reported an error.";

export const MISSING_PARAMS_MESSAGE =
  "Missing authorization code or state parameter.";

export const NO_ORG_MESSAGE =
  "No organization context. Sign in, then start the connection again.";

export interface OAuthCallbackViewInput {
  code: string | null;
  state: string | null;
  providerError: string | null;
  providerErrorDescription: string | null;
  orgId: string | undefined;
}

export interface OAuthCallbackError {
  status: "error";
  message: React.ReactNode;
  /**
   * Whether the callback params should be removed from the URL. Only a code the provider has
   * already spent has to go: re-sending one gets `invalid_code` back in place of the real
   * failure. An unspent code stays, for every provider, because reloading the URL is the only
   * way a user recovers once the precondition that blocked it is met — signing in and
   * reloading is what turns a missing org into a completable install.
   */
  stripQuery: boolean;
  /**
   * Whether the redirect URL alone determines this error, so nothing that resolves later — a
   * restored session, a late org — can change it. A callback that exchanges in the page
   * reports these on the first render instead of holding them behind the auth-settling wait.
   */
  paramLevel: boolean;
}

/** What the loader does next once it has read the URL and resolved an org. */
export type OAuthCallbackResolution =
  | { status: "exchange"; code: string; state: string; orgId: string }
  | OAuthCallbackError;

/** What the callback page renders once the flow has settled. */
export type OAuthCallbackView = { status: "success" } | OAuthCallbackError;

/**
 * Maps the callback params and the resolved org to either the exchange the loader should run
 * or the error it should report instead. Pure — each earlier branch wins, so a request that
 * never carried a code is never reported as a failed exchange.
 */
export function resolveOAuthCallbackView(
  input: OAuthCallbackViewInput,
): OAuthCallbackResolution {
  const { code, state, providerError, providerErrorDescription, orgId } = input;

  if (providerError) {
    return {
      status: "error",
      message:
        OAUTH_ERROR_MESSAGES[providerError] ??
        (providerErrorDescription || GENERIC_PROVIDER_ERROR),
      stripQuery: false,
      paramLevel: true,
    };
  }
  if (!code || !state) {
    return {
      status: "error",
      message: MISSING_PARAMS_MESSAGE,
      stripQuery: false,
      paramLevel: true,
    };
  }
  if (orgId === undefined) {
    return {
      status: "error",
      message: NO_ORG_MESSAGE,
      stripQuery: false,
      paramLevel: false,
    };
  }
  return { status: "exchange", code, state, orgId };
}

/** A settled exchange attempt: the API either accepted the code or refused it. */
export type OAuthExchangeOutcome = { ok: true } | { ok: false; error: unknown };

/**
 * The view for an exchange that has run, whichever way it went. Shared so a loader and an
 * in-page query cannot disagree about what a refusal looks like or whether it strips.
 */
export function resolveExchangeOutcomeView(
  outcome: OAuthExchangeOutcome,
): OAuthCallbackView {
  if (outcome.ok) {
    return { status: "success" };
  }
  return {
    status: "error",
    message: userFacingErrorMessage(outcome.error),
    stripQuery: true,
    paramLevel: false,
  };
}

/** A view that can still change: the callback is mid-flight. */
export type OAuthCallbackPendingView =
  { status: "loading" } | OAuthCallbackView;

export interface OAuthExchangeViewInput extends OAuthCallbackViewInput {
  /** False while session restore or a sign-in is still in flight. */
  authSettled: boolean;
  exchangeStatus: "pending" | "error" | "success";
  exchangeError: unknown;
}

/**
 * The same precedence for a callback whose exchange runs in the page rather than in a route
 * loader, so a provider without a data-router route (MCP) keeps the auth-settling wait a
 * loader gets for free. Pure.
 */
export function resolveOAuthExchangeView(
  input: OAuthExchangeViewInput,
): OAuthCallbackPendingView {
  const { authSettled, exchangeStatus } = input;
  const resolution = resolveOAuthCallbackView(input);

  // The resolver decides which of its errors the URL alone determines; re-deriving that here
  // would let a branch it adds later go unrecognised and sit behind the wait below.
  if (resolution.status === "error" && resolution.paramLevel) {
    return resolution;
  }
  // Anything below turns on the org, which the boot check has not resolved yet.
  if (!authSettled) {
    return { status: "loading" };
  }
  if (resolution.status === "error") {
    return resolution;
  }
  if (exchangeStatus === "pending") {
    return { status: "loading" };
  }
  if (exchangeStatus === "error") {
    return resolveExchangeOutcomeView({
      ok: false,
      error: input.exchangeError,
    });
  }
  return resolveExchangeOutcomeView({ ok: true });
}
